import Modal from "../../../common/ModalPopup";
import { ChangeEvent, DragEvent, useState } from "react";
import { useLanguage } from "@src/context/LanguageContext";
import { LuImageUp } from "react-icons/lu";
import FullPageLoader from "../../../common/FullPageLoader";
import { backOrderDocUpload } from "@src/store/slices/insurance";
import { STATUS_SUCCESS } from "@src/config/constant";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const MAX_UPLOAD_FILES = 10;
const UploadIcon: any = LuImageUp;

const SendBackToAgentPopup = (props: any) => {
    const [errors, setError] = useState<any>({});
    const [formData, setFormData] = useState<any>({});
    const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const [loading, setLoading] = useState(false);
    const insuranceState = useSelector((state: any) => state.insurance);
    const {translations} = useLanguage();

    const handleChange = (key: string, event: any) => {
        formData[key] = event.target.value;
        errors[key] = "";
        setError({ ...errors });
        setFormData({ ...formData });
    }

    const getFileType = (file: File) => {
        if (file.type.includes("image")) return "image";

        let fileType: any = file.name.split(".");
        fileType = fileType && fileType[fileType.length - 1];
        fileType = fileType && fileType.toLowerCase();

        return ["csv", "xls", "xlsx"].includes(fileType) ? "excel" : (fileType === "doc" || fileType === "docx") ? "word" : fileType;
    }

    const addSelectedFiles = (files: FileList | File[]) => {
        const newFiles = Array.from(files || []);

        if (selectedFiles.length + newFiles.length > MAX_UPLOAD_FILES) {
            setError({ ...errors, images: `Maximum ${MAX_UPLOAD_FILES} files are allowed` });
            return;
        }

        setError({ ...errors, images: "" });

        const filesToAdd = newFiles.map((file: File) => ({
            file,
            url: URL.createObjectURL(file),
        }));

        setSelectedFiles([...selectedFiles, ...filesToAdd]);
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) addSelectedFiles(event.target.files);
        event.target.value = "";
    }

    const removeSelectedFile = (fileIndex: number) => {
        const removedFile = selectedFiles[fileIndex];
        if (removedFile?.url) URL.revokeObjectURL(removedFile.url);

        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(fileIndex, 1);
        errors["images"] = "";
        setError({ ...errors });
        setSelectedFiles(updatedFiles);
    }

    const clearForm = () => {
        selectedFiles.forEach((file: any) => {
            if (file?.url) URL.revokeObjectURL(file.url);
        });
        setSelectedFiles([]);
        setFormData({});
        setError({});
        setDragOver(false);
    }

    const closePopup = () => {
        clearForm();
        props.handleClose();
    }

    const isFormValid = () => {
        let isValid = true;
        let updatedErrors: any = {};

        if (!formData.remarks || formData.remarks.trim() === "") {
            updatedErrors["remarks"] = "This Field is Required";
            isValid = false;
        }

        if (selectedFiles.length > MAX_UPLOAD_FILES) {
            updatedErrors["images"] = `Maximum ${MAX_UPLOAD_FILES} files are allowed`;
            isValid = false;
        } else if (errors.images) {
            updatedErrors["images"] = errors.images;
            isValid = false;
        }

        setError(updatedErrors);
        return isValid;
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (loading || !isFormValid()) return;

        const leadId = insuranceState?.leadDetail?.leadId || insuranceState?.leadDetail?.id;

        if (!leadId) {
            toast.error("Lead id is required");
            return;
        }

        const submitFormData = new FormData();
        submitFormData.append("lead_id", `${leadId}`);
        submitFormData.append("back_order_desc", formData.remarks.trim());

        selectedFiles.forEach((selectedFile: any) => {
            if (selectedFile?.file) submitFormData.append("documents", selectedFile.file);
        });

        setLoading(true);
        try {
            const res: any = await backOrderDocUpload(submitFormData);
            setLoading(false);
            if (res && res.status === STATUS_SUCCESS) {
                closePopup();
                if (props.getDetails) props.getDetails();
            }
        } catch (error) {
            setLoading(false);
            toast.error("Something went wrong");
        }
    }

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(true);
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(true);
    }

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
    }

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        addSelectedFiles(e.dataTransfer.files);
    }

    return (
        <div className="view-timeline-popup image-timeline-popup send-back-agent-popup">
            <FullPageLoader show={loading} />
            <Modal show={props.show} handleClose={closePopup} >
                <div className="modal-header">
                    <h2>{translations.commonMsg?.['Upload Documents'] || "Upload Documents"}</h2>
                </div>
                <div className="modal-body" >
                    <div className="row">
                        <div className="col-md-12">
                            <div className="image-uploader-wrapper">
                                <div
                                    className={dragOver ? "display-box drag-over" : "display-box"}
                                    onDragEnter={handleDragEnter}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <div className="icon-text-box">
                                        <UploadIcon className="send-back-upload-icon" />
                                        <div className="upload-txt">
                                            <h4>Drop Files Here to Upload</h4>
                                            <div className="or-txt">OR</div>
                                            <button
                                                type="button"
                                                className="btn-primary file-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    document.getElementById("send-back-agent-file-input")?.click();
                                                }}
                                            >
                                                Browse File
                                            </button>
                                        </div>
                                        <input
                                            id="send-back-agent-file-input"
                                            type="file"
                                            name="documents"
                                            className="upload-image-input"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <span className="error-txt send-back-upload-error">
                                {errors.images || ""}
                            </span>
                        </div>

                        {
                            selectedFiles.length
                                ?
                                <div className="col-md-12 send-back-file-list">
                                    {
                                        selectedFiles.map((selectedFile: any, index: number) => {
                                            const fileType = getFileType(selectedFile.file);
                                            return (
                                                <div className="send-back-file-item" key={`${selectedFile.file.name}-${index}`}>
                                                    <div role="presentation" className="upload-image-proof">
                                                        <span className='close-icn' onClick={() => removeSelectedFile(index)}>
                                                            <i className='ic-clearclose'></i>
                                                        </span>
                                                        {
                                                            fileType === "image"
                                                                ? <img src={selectedFile.url} alt={selectedFile.file.name} />
                                                                : <i className={`ic-${fileType}-file icons-normal`}></i>
                                                        }
                                                    </div>
                                                    <p className='doc-tag-name' title={selectedFile.file.name}>{selectedFile.file.name}</p>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                :
                                null
                        }

                        <div className="col-md-12">
                            <fieldset className="form-filed">
                                <div className="material">
                                    <textarea
                                        id="send-back-agent-remarks"
                                        placeholder="Description for Back Order"
                                        name="remarks"
                                        className="form-input"
                                        value={formData.remarks || ""}
                                        onChange={(e: any) => handleChange("remarks", e)}
                                    />
                                    <span className="error-txt">
                                        {errors.remarks || ""}
                                    </span>
                                </div>
                            </fieldset>
                        </div>

                        <div className="col-md-12">
                            <button className="btn-primary send-back-submit-btn" onClick={(e: any) => handleSubmit(e)} disabled={loading}>
                                {translations.commonMsg?.['Send Back to Agent'] || "Send Back to Agent"}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default SendBackToAgentPopup;
